import { useAuth } from "@/src/contexts/auth.context";
import { RankedLeaderboardEntry } from "@/src/models/leaderboard.model";
import { useTheme } from "@/src/styles/theme-provider";
import { MedalStar, Star } from "iconsax-react-nativejs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutChangeEvent, Pressable, Text, View } from "react-native";
import BlocksAvatar from "@/src/components/ui-kit/blocks-avatar";
import { Bone, SkeletonPulse } from "@/src/components/ui-kit/skeleton";

type LeaderboardPodiumProps = Readonly<{
  entries: ReadonlyArray<RankedLeaderboardEntry>;
  selectedUserId: string | null;
  onPress: (userId: string) => void;
}>;

type PodiumCardProps = Readonly<{
  entry: RankedLeaderboardEntry;
  isFirst: boolean;
  isSelected: boolean;
  isCurrentUser: boolean;
  cardWidth: number;
  bgClass: string;
  cardTextColor: string;
  cardTextMutedColor: string;
  primaryColor: string;
  onPress: (userId: string) => void;
}>;

const PODIUM_ORDER = [2, 1, 3] as const;
/** Display order of the first three standings by position (2nd · 1st · 3rd); ties keep their shared rank. */
const PODIUM_POSITIONS = [1, 0, 2] as const;

const PODIUM_TAILWIND_BG: Readonly<Record<number, string>> = {
  1: "bg-ash",
  2: "bg-ash",
  3: "bg-ash",
};

const RANK_BADGE_BG: Readonly<Record<number, string>> = {
  1: "bg-wheat",
  2: "bg-ash-3",
  3: "bg-coral",
};

const CARD_GAP = 12;
const CARD_MAX_WIDTH = 200;
const CARD_MIN_WIDTH = 100;
const NARROW_THRESHOLD = 130;

const PodiumCard = ({
  entry,
  isFirst,
  isSelected,
  isCurrentUser,
  cardWidth,
  bgClass,
  cardTextColor,
  cardTextMutedColor,
  primaryColor,
  onPress,
}: PodiumCardProps) => {
  const { t } = useTranslation();

  const isNarrow = cardWidth < NARROW_THRESHOLD;
  const avatarSize = isFirst ? 64 : 52;
  const badgeBg = RANK_BADGE_BG[entry?.rank];
  const formattedPoints = entry?.points?.toLocaleString() ?? "0";
  const achievementsCount = entry?.achievements ?? 0;
  const displayName = isCurrentUser
    ? t("LABEL_YOU")
    : (entry?.displayName ?? "");

  return (
    <View className="justify-end" style={{ width: cardWidth }}>
      <Pressable
        onPress={() => onPress(entry?.userId)}
        className={`rounded-2xl items-center w-full
          ${bgClass}
          ${isFirst ? "pt-7 pb-7" : "pt-4 pb-3"}
          ${isSelected ? "border-2 border-white/50 opacity-90" : "opacity-100"}
          hover:bg-ash-3
        `}
      >
        {/* Avatar */}
        <View className="relative mb-2">
          <BlocksAvatar
            userId={entry?.userId}
            fallbackImageUrl={entry?.avatar}
            name={entry?.displayName}
            size={avatarSize}
            className="border-2 border-white/50"
          />

          {badgeBg && (
            <View
              className={`absolute bottom-0 right-0 w-5 h-5 rounded-full items-center justify-center border-2 border-white/50 ${badgeBg}`}
            >
              <Text className="text-onyx font-bold text-xs">{entry?.rank}</Text>
            </View>
          )}
        </View>

        <Text
          className="font-semibold text-center w-full text-sm px-2"
          style={{ color: cardTextColor }}
          numberOfLines={1}
        >
          {displayName}
        </Text>

        <View
          className={`mt-2 w-full justify-center ${
            isNarrow ? "flex-col items-center gap-1" : "flex-row gap-2"
          }`}
        >
          <View className={`items-center ${isNarrow ? "" : "flex-1"}`}>
            <View className="flex-row items-center gap-1">
              <Star size={14} color={primaryColor} variant="Bold" />
              <Text
                className="font-bold text-base"
                style={{ color: cardTextColor }}
                numberOfLines={1}
              >
                {formattedPoints}
              </Text>
            </View>
            <Text
              className="tracking-wider text-xs"
              style={{ color: cardTextMutedColor }}
            >
              {t("LABEL_PTS")}
            </Text>
          </View>
          <View className={`items-center ${isNarrow ? "" : "flex-1"}`}>
            <View className="flex-row items-center gap-1">
              <MedalStar size={14} color={primaryColor} variant="Bold" />
              <Text
                className="font-bold text-base"
                style={{ color: cardTextColor }}
                numberOfLines={1}
              >
                {achievementsCount}
              </Text>
            </View>
            <Text
              className="tracking-wider text-xs"
              style={{ color: cardTextMutedColor }}
            >
              {t("LABEL_BADGES")}
            </Text>
          </View>
        </View>
      </Pressable>
    </View>
  );
};

/**
 * Placeholder for the podium while the leaderboard loads: three cards in
 * podium order (2 · 1 · 3), the middle one taller with a bigger avatar, same
 * as PodiumCard — so the page doesn't jump when the real cards land.
 */
export const LeaderboardPodiumSkeleton = () => (
  <SkeletonPulse style={{ width: "100%" }} testID="leaderboard-podium-skeleton">
    <View className="w-full flex-row justify-center items-end gap-2">
      {PODIUM_ORDER.map((rank) => {
        const isFirst = rank === 1;
        return (
          <View
            key={rank}
            className={`flex-1 rounded-2xl items-center bg-ash ${
              isFirst ? "pt-7 pb-7" : "pt-4 pb-3"
            }`}
            style={{ maxWidth: CARD_MAX_WIDTH, minWidth: CARD_MIN_WIDTH }}
          >
            <Bone
              className={`rounded-full mb-2 ${isFirst ? "h-16 w-16" : "h-[52px] w-[52px]"}`}
            />
            <Bone className="h-3 w-20" />
            <View className="mt-3 flex-row gap-4">
              <View className="items-center gap-1">
                <Bone className="h-4 w-10" />
                <Bone className="h-2 w-6" />
              </View>
              <View className="items-center gap-1">
                <Bone className="h-4 w-10" />
                <Bone className="h-2 w-6" />
              </View>
            </View>
          </View>
        );
      })}
    </View>
  </SkeletonPulse>
);

export const LeaderboardPodium = ({
  entries,
  selectedUserId,
  onPress,
}: LeaderboardPodiumProps) => {
  const [containerWidth, setContainerWidth] = useState(0);
  const { themeVars } = useTheme();
  const { user } = useAuth();
  const currentUserId = user?.itemId ?? "";

  const cardTextColor = themeVars["--color-onyx-default"] as string;
  const cardTextMutedColor = `${themeVars["--color-onyx-default"]}99` as string;
  const primaryColor = themeVars["--color-primary-default"] as string;

  const top3 = PODIUM_POSITIONS.map((position) => entries[position]).filter(
    Boolean,
  ) as RankedLeaderboardEntry[];

  const handleLayout = (e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  };

  const totalGap = CARD_GAP * 2;
  const rawCardWidth =
    containerWidth > 0 ? (containerWidth - totalGap) / 3 : CARD_MIN_WIDTH;
  const cardWidth = Math.min(
    Math.max(rawCardWidth, CARD_MIN_WIDTH),
    CARD_MAX_WIDTH,
  );

  return (
    <View
      className="w-full flex-row justify-center gap-2"
      onLayout={handleLayout}
    >
      {top3.map((podiumEntry) => {
        const isFirst = podiumEntry === entries[0];
        const isSelected = selectedUserId === podiumEntry?.userId;
        const isCurrentUser = podiumEntry?.userId === currentUserId;
        const bgClass = PODIUM_TAILWIND_BG[podiumEntry?.rank] ?? "bg-ash";

        return (
          <PodiumCard
            key={podiumEntry?.userId}
            entry={podiumEntry}
            isFirst={isFirst}
            isSelected={isSelected}
            isCurrentUser={isCurrentUser}
            cardWidth={cardWidth}
            bgClass={bgClass}
            cardTextColor={cardTextColor}
            cardTextMutedColor={cardTextMutedColor}
            primaryColor={primaryColor}
            onPress={onPress}
          />
        );
      })}
    </View>
  );
};
